const alreadyWarned = new Set()

export default defineNuxtPlugin({
  name: 'permissions',
  dependsOn: ['i18n', 'store'],
  async setup(nuxtApp) {
    const { $registry, $store } = nuxtApp

    let hasPermission
    let canNavCapability

    if (import.meta.env.VITEST) {
      hasPermission = () => true
      canNavCapability = () => true
    } else {
      hasPermission = (operation, context, workspaceId = null) => {
        let perms

        if (workspaceId === null) {
          perms = $store.getters['auth/getGlobalUserPermissions']
        } else {
          const workspace = $store.getters['workspace/get'](workspaceId)
          if (workspace === undefined || !workspace._.permissionsLoaded) {
            perms = $store.getters['auth/getGlobalUserPermissions']
          } else {
            perms = workspace._.permissions
          }
        }

        for (const perm of perms) {
          const { name, permissions } = perm
          let manager

          try {
            manager = $registry.get('permissionManager', name)
          } catch (e) {
            // If a permission manager is missing we show a warning once.
            if (!alreadyWarned.has(name)) {
              alreadyWarned.add(name)
              console.warn(`Permission manager '${name}' missing in registry`)
            }
            continue
          }

          try {
            const result = manager.hasPermission(
              permissions,
              operation,
              context,
              workspaceId
            )

            if ([true, false].includes(result)) {
              return result
            }
          } catch (e) {
            console.warn('Error during permission check', e)
            return false
          }
        }
        return false
      }

      /**
       * Intersection-based navigation capability (workspace custom roles × RBAC ceiling).
       * @param {string} capabilityId e.g. nav.agents or table_menu_entry:12
       * @param {number} workspaceId
       * @param {'can_read'|'can_create'|'can_update'|'can_delete'} [action]
       */
      canNavCapability = (capabilityId, workspaceId, action = 'can_read') => {
        if (!capabilityId) {
          return true
        }
        const workspace = $store.getters['workspace/get'](workspaceId)
        // Deny until workspace_navigation is known: returning true here caused a visible
        // flash during permission refetch or first paint.
        if (workspace === undefined || !workspace._.permissionsLoaded) {
          return false
        }
        const perms = workspace._.permissions
        const block = perms.find((p) => p.name === 'workspace_navigation')
        if (!block || !block.permissions || !block.permissions.capabilities) {
          return true
        }
        const cap = block.permissions.capabilities[capabilityId]
        if (!cap) {
          return false
        }
        return cap[action] === true
      }
    }

    nuxtApp.provide('hasPermission', hasPermission)
    nuxtApp.provide('canNavCapability', canNavCapability)
  },
})
